import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { Question } from '@mentimeter/http-clients';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useViewSizeChange } from './hooks';
import {
  NumericalScale,
  type ScoreData,
} from './numerical-scale/NumericalScale';
import { stepToDisplayValue } from './numerical-scale/transformValues';

interface NumericalQuestionVisualizationProps {
  question: Question;
  results: ScoreData | undefined;
  shouldShowCorrect: boolean;
  isScreenshotMode: boolean;
  displayMin: number;
  max: number;
  scale: number;
  errorMargin: number;
  hasErrorMargin: boolean;
  correctAnswer: number;
}

export const NumericalQuestionVisualization = ({
  question,
  results,
  shouldShowCorrect,
  isScreenshotMode,
  displayMin,
  max,
  scale,
  errorMargin,
  hasErrorMargin,
  correctAnswer,
}: NumericalQuestionVisualizationProps) => {
  const { size, container } = useViewSizeChange(question);
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();

  const displayMax = stepToDisplayValue({
    step: max,
    scale,
    min: displayMin,
  });

  return (
    <Box
      width="100%"
      justifyContent="center"
      alignItems="center"
      flexDirection="row"
    >
      <Box style={{ flexWrap: 'wrap' }}>
        <Text
          fontSize="200"
          fontWeight="semiBold"
          data-testid="presentation-range-min-value"
        >
          {displayMin}
        </Text>
      </Box>
      <Box width="60%" height="550px" px={2} zIndex={1}>
        {question.choices[0] && (
          <div
            ref={container}
            style={{
              width: '100%',
              height: '100%',
            }}
            // For size changes, force re-render the visualization
            key={`${size.width}-${size.height}`}
          >
            <NumericalScale
              baseFontSize={12}
              textColor={textColor}
              data={results}
              fills={fillColors}
              lineColor={textColor}
              showCorrect={shouldShowCorrect}
              hasErrorMargin={hasErrorMargin}
              displayMin={displayMin}
              maxStep={max}
              errorMargin={errorMargin}
              correctAnswer={correctAnswer}
              isScreenshotMode={isScreenshotMode}
              scale={scale}
            />
          </div>
        )}
      </Box>
      <Box style={{ flexWrap: 'wrap' }}>
        <Text
          fontSize="200"
          fontWeight="semiBold"
          data-testid="presentation-range-max-value"
        >
          {displayMax}
        </Text>
      </Box>
    </Box>
  );
};
